import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, getErrMsg } from "../lib/api";
import { formatTL, formatDate } from "../lib/format";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { AlertTriangle, Banknote, FileBarChart2, Printer } from "lucide-react";
import { donemEtiketi } from "./AyYilSecici";
import { takipRaporuYazdir } from "../lib/takipRaporYazdir";
import { useAuth } from "../context/AuthContext";

const DURUM_KARTLARI = [
  { key: "tam_odenen", label: "Tam Ödenen", renk: "text-emerald-700", zemin: "bg-emerald-50 border-emerald-200" },
  { key: "kismi", label: "Kısmi / Eksik Ödeme", renk: "text-amber-700", zemin: "bg-amber-50 border-amber-200" },
  { key: "odenmemis", label: "Hiç Ödenmemiş", renk: "text-slate-700", zemin: "bg-slate-50 border-slate-200" },
  { key: "vadesi_gecen", label: "Vadesi Geçen", renk: "text-red-700", zemin: "bg-red-50 border-red-200" },
];

const KirilimTablo = ({ baslik, satirlar, testid }) => (
  <div className="rounded-lg border overflow-hidden break-inside-avoid" data-testid={testid}>
    <p className="bg-muted/40 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{baslik}</p>
    {satirlar.length === 0 ? (
      <p className="px-3 py-3 text-sm text-muted-foreground">Açık bakiye yok.</p>
    ) : (
      <table className="w-full text-sm">
        <tbody>
          {satirlar.map((s) => (
            <tr key={s.ad} className="border-t border-border/60">
              <td className="px-3 py-2 truncate max-w-[220px]" title={s.ad}>{s.ad}</td>
              <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{s.adet} dosya</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold whitespace-nowrap">{formatTL(s.kalan, 2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

export const TakipRaporu = ({ yil, ay, yenileTetik = 0, onTahsilat }) => {
  const { user } = useAuth();
  const [rapor, setRapor] = useState(null);
  const [loading, setLoading] = useState(false);

  const yazdir = () => {
    if (!takipRaporuYazdir(rapor, donemEtiketi(yil, ay), user?.ad_soyad || user?.email)) {
      toast.error("Yazdırma penceresi açılamadı. Tarayıcı açılır pencere engelini kontrol edin.");
    }
  };

  const fetchRapor = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/ciro/takip-rapor", { params: { yil, ay: ay || undefined } });
      setRapor(res.data);
    } catch (err) {
      toast.error(getErrMsg(err, "Takip raporu yüklenemedi."));
    } finally {
      setLoading(false);
    }
  }, [yil, ay]);

  useEffect(() => {
    fetchRapor();
  }, [fetchRapor, yenileTetik]);

  if (loading && !rapor) return <Skeleton className="h-48" />;
  if (!rapor) return null;
  const g = rapor.genel;

  return (
    <div className="rounded-xl border bg-card p-4 space-y-4" data-testid="takip-raporu">
      <div className="flex flex-wrap items-center gap-3">
        <FileBarChart2 className="h-4 w-4 text-muted-foreground" />
        <div>
          <h3 className="font-display text-sm font-semibold">Takip Raporu — {donemEtiketi(yil, ay)}</h3>
          <p className="text-xs text-muted-foreground">Bu dönemde kesilen dosyaların ödeme durumu (Ağustos öncesi devir hariç)</p>
        </div>
        <Button variant="outline" onClick={yazdir} className="ml-auto h-10 gap-1.5" data-testid="takip-rapor-yazdir-button">
          <Printer className="h-4 w-4" /> Yazdır / PDF
        </Button>
      </div>

      <div className="space-y-4">
        {/* Genel + durum kırılımı */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="takip-rapor-durumlar">
          <div className="rounded-lg border bg-muted/30 p-3" data-testid="takip-rapor-genel">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Kesilen Dosya</p>
            <p className="mt-1 font-display text-lg font-bold tabular-nums">{formatTL(g.fatura_tutari)}</p>
            <p className="text-[10px] text-muted-foreground">{g.adet} dosya · Tahsilat %{g.tahsilat_orani.toLocaleString("tr-TR")}</p>
            <div className="mt-2 h-1.5 w-full rounded-full bg-muted overflow-hidden">
              <div className="h-full rounded-full bg-emerald-500" style={{ width: `${Math.min(g.tahsilat_orani, 100)}%` }} />
            </div>
          </div>
          {DURUM_KARTLARI.map((k) => {
            const d = rapor.durumlar[k.key];
            return (
              <div key={k.key} className={`rounded-lg border p-3 ${d.adet > 0 ? k.zemin : "bg-muted/30"}`} data-testid={`takip-rapor-${k.key}`}>
                <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{k.label}</p>
                <p className={`mt-1 font-display text-lg font-bold tabular-nums ${d.adet > 0 ? k.renk : ""}`}>
                  {k.key === "tam_odenen" ? formatTL(d.tahsil_edilen) : formatTL(d.kalan)}
                </p>
                <p className="text-[10px] text-muted-foreground">
                  {d.adet} dosya{k.key !== "tam_odenen" && d.adet > 0 ? ` · fatura ${formatTL(d.fatura_tutari)}` : ""}
                </p>
              </div>
            );
          })}
        </div>

        {/* Eksik ödeme listesi */}
        <div className="rounded-lg border overflow-hidden">
          <div className="flex items-center justify-between bg-muted/40 px-3 py-2">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Eksik Ödemeler — Açık Kalan Dosyalar</p>
            <p className="text-xs font-semibold tabular-nums" data-testid="takip-rapor-eksik-toplam">Toplam eksik: {formatTL(g.kalan, 2)}</p>
          </div>
          <div className="w-full overflow-x-auto">
            <table className="w-full text-sm" data-testid="takip-rapor-eksik-table">
              <thead className="bg-muted/20">
                <tr className="text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="px-3 py-2">Tarih</th>
                  <th className="px-3 py-2">Fatura No</th>
                  <th className="px-3 py-2">İş Emri</th>
                  <th className="px-3 py-2">Plaka</th>
                  <th className="px-3 py-2">Dosya No</th>
                  <th className="px-3 py-2">Sigorta / Müşteri</th>
                  <th className="px-3 py-2 text-right">Fatura</th>
                  <th className="px-3 py-2 text-right">Tahsil Edilen</th>
                  <th className="px-3 py-2 text-right">Eksik Kalan</th>
                  <th className="px-3 py-2">Vade</th>
                  <th className="px-3 py-2">Durum</th>
                  {onTahsilat && <th className="px-3 py-2 text-right print:hidden">İşlem</th>}
                </tr>
              </thead>
              <tbody>
                {rapor.eksik_odemeler.length === 0 ? (
                  <tr>
                    <td colSpan={12} className="px-3 py-8 text-center text-muted-foreground" data-testid="takip-rapor-eksik-bos">
                      Bu dönemde eksik ödeme yok — tüm dosyalar tahsil edilmiş.
                    </td>
                  </tr>
                ) : (
                  rapor.eksik_odemeler.map((e) => (
                    <tr key={e.id} className={`border-t border-border/60 ${e.gecikme_gun != null ? "bg-red-50/40" : ""}`} data-testid="takip-rapor-eksik-row">
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{formatDate(e.tarih)}</td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums">{e.belge_no || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums text-sky-700">{e.is_emri_no || "—"}</td>
                      <td className="px-3 py-2 whitespace-nowrap font-semibold uppercase tracking-wide" data-testid="takip-rapor-plaka">{e.plaka || <span className="font-normal text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2 whitespace-nowrap tabular-nums" data-testid="takip-rapor-dosya-no">{e.dosya_no || <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-3 py-2 max-w-[200px]">
                        <p className="truncate" title={e.sigorta_sirketi || e.musteri}>{e.sigorta_sirketi || e.musteri || "—"}</p>
                        {e.sigorta_sirketi && e.musteri && e.sigorta_sirketi !== e.musteri && (
                          <p className="truncate text-[11px] text-muted-foreground">{e.musteri}</p>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{formatTL(e.fatura_tutari, 2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap text-emerald-700">{formatTL(e.tahsil_edilen, 2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap font-semibold text-amber-800">
                        {formatTL(e.eksik, 2)}
                        {e.kdv_haric_tahsilat && <span className="ml-1 rounded bg-amber-100 px-1 py-0.5 text-[9px] font-medium text-amber-800 align-middle">KDV'siz</span>}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap text-xs">
                        {e.vade_tarihi ? formatDate(e.vade_tarihi) : <span className="italic text-muted-foreground">Vade yok</span>}
                        {e.gecikme_gun != null && (
                          <span className="ml-1 inline-flex items-center gap-0.5 text-red-700"><AlertTriangle className="h-3 w-3" />{e.gecikme_gun} gün</span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge className={`border text-[10px] hover:bg-transparent ${e.durum === "Kısmi" ? "bg-amber-50 text-amber-800 border-amber-300" : "bg-slate-50 text-slate-700 border-slate-300"}`}>
                          {e.durum}
                        </Badge>
                      </td>
                      {onTahsilat && (
                        <td className="px-3 py-2 text-right print:hidden">
                          <Button variant="ghost" size="sm" onClick={() => onTahsilat(e.id)} className="h-8 gap-1 text-emerald-700 hover:bg-emerald-50 hover:text-emerald-800" data-testid="takip-rapor-tahsilat-button">
                            <Banknote className="h-3.5 w-3.5" /> Tahsilat
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          <KirilimTablo baslik="Sigorta / Müşteri Bazında Açık Bakiye" satirlar={rapor.sigorta_bazinda} testid="takip-rapor-sigorta" />
          <KirilimTablo baslik="Kanal Bazında Açık Bakiye" satirlar={rapor.kanal_bazinda} testid="takip-rapor-kanal" />
        </div>
      </div>
    </div>
  );
};
